import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/auth-provider';
import { TokenAuthHandler } from './components/auth/token-auth-handler';
import { ProtectedRoute } from './components/layout/protected-route';
import { SuperAdminRoute } from './components/layout/super-admin-route';
import { SuperOrDataAdminRoute } from './components/layout/super-or-data-admin-route';
import { ProsciAdminRoute } from './components/layout/prosci-admin-route';
import { KaiyaAdminRoute } from './components/layout/kaiya-admin-route';
import { LicenseAdminRoute } from './components/layout/license-admin-route';
import { AdminLayout } from './components/layout/admin-layout';
import { Toaster } from 'prosci-ui';
import { IntercomProvider } from './providers/intercom-provider';
import { ChameleonProvider } from './providers/chameleon-provider';
import { AdminUsersPage } from './pages/admin-users';
import { AccountsPage } from './pages/accounts';
import { AccountDetailPage } from './pages/accounts/[id]';
import { UsersPage } from './pages/users';
import { UserFormPage } from './pages/users/[id]';
import { BatchUploadPage } from './pages/users/batch-upload';
import { BulkDeletePage } from './pages/users/bulk-delete';
import { ReportsPage } from './pages/reports';
import { UserListByToolReport } from './pages/reports/user-list-by-tool';
import { PortalActivityReport } from './pages/reports/portal-activity';
import { LoginCountReport } from './pages/reports/login-count';
import { ElearningTrackingReport } from './pages/reports/elearning-tracking';
import { UserListReport } from './pages/reports/user-list';
import { DeletedUsersReport } from './pages/reports/deleted-users';
import { WorkspaceEntitlementsReport } from './pages/reports/workspace-entitlements';
import { OrganizationListReport } from './pages/reports/organization-list';
import { PcacpExamResultsReport } from './pages/reports/pcacp-exam-results';
import { SettingsPage } from './pages/settings';
import { IntegrationsPage } from './pages/integrations';
import { KaiyaSkillsPage } from './pages/kaiya-skills';
import { SkillTemplateEditorPage } from './pages/kaiya-skills/editor';
import { KaiyaConfigPage } from './pages/kaiya-config';
import { KaiyaDomainsPage } from './pages/kaiya-domains';
import { KaiyaAgentSettingsPage } from './pages/kaiya-settings';
import { KaiyaPromptsPage } from './pages/kaiya-prompts';
import { KaiyaTerminologyPage } from './pages/kaiya-terminology';
import { KaiyaDashboardPage } from './pages/kaiya-dashboard';
import { KaiyaEvalsPage } from './pages/kaiya-evals';
import { KaiyaPlaygroundPage } from './pages/kaiya-playground';
import { KaiyaGoldenDocsPage } from './pages/kaiya-golden-docs';
import { KaiyaGuidedUseCasesPage } from './pages/kaiya-guided-use-cases';
import { KaiyaGuidedCanvasPage } from './pages/kaiya-guided-use-cases/canvas';
import { KaiyaElementStandardEditorPage } from './pages/kaiya-guided-use-cases/editor';
import { KaiyaReviewQueuePage } from './pages/kaiya-review-queue';
import { useAuthToken } from './hooks/use-auth-token';
import { useCurrentUser } from './hooks/use-current-user';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { sessionAuthService } from './services/session-auth-service';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: (failureCount, error: any) => {
        // If it's an auth error and we haven't retried yet, try once more
        if (error?.status === 401 && failureCount < 1) {
          // Reinitialize session token
          const token = sessionAuthService.getSessionToken();
          if (token) {
            (window as any).__PROSCI_SESSION_TOKEN__ = token;
          }
          return true;
        }
        return false;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function CustomerLoginRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    // TokenAuthHandler will process the access token from URL
    // Then redirect to users page
    const timer = setTimeout(() => {
      navigate('/users');
    }, 100);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Logging in...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  // Call useAuthToken here - it will handle auth state changes
  useAuthToken();
  // Fetch and store the current user ID for API requests
  useCurrentUser();
  // Proactively refresh tokens to prevent session expiration during activity
  useTokenRefresh();

  return (
    <Routes>
      {/* Customer login route - for license admin access from portal */}
      <Route path="/customers/login" element={<CustomerLoginRedirect />} />

      {/* All routes are protected */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AdminLayout />}>
          <Route path="/users" element={<UsersPage />} />
          <Route path="/users/new" element={<UserFormPage />} />
          <Route path="/users/:id/edit" element={<UserFormPage />} />
          <Route path="/users/batch-upload" element={<BatchUploadPage />} />

          {/* License Admin only routes */}
          <Route element={<LicenseAdminRoute />}>
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
          </Route>

          {/* Prosci Admin only routes (not accessible to License Admins) */}
          <Route element={<ProsciAdminRoute />}>
            <Route path="/users/bulk-delete" element={<BulkDeletePage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/accounts/:id" element={<AccountDetailPage />} />
            <Route path="/reports/deleted-users" element={<DeletedUsersReport />} />
            <Route path="/reports/organization-list" element={<OrganizationListReport />} />
            <Route element={<SuperOrDataAdminRoute />}>
              <Route path="/reports/pcacp-exam-results" element={<PcacpExamResultsReport />} />
            </Route>

            {/* Kaiya section — super, admin, affiliate_admin */}
            <Route element={<KaiyaAdminRoute />}>
              <Route path="/kaiya-skills" element={<KaiyaSkillsPage />} />
              <Route path="/kaiya-skills/new" element={<SkillTemplateEditorPage />} />
              <Route path="/kaiya-skills/:id/edit" element={<SkillTemplateEditorPage />} />
              <Route path="/kaiya-config" element={<Navigate to="/kaiya-domains" replace />} />
              <Route path="/kaiya-domains" element={<KaiyaDomainsPage />} />
              <Route path="/kaiya-terminology" element={<KaiyaTerminologyPage />} />
              <Route path="/kaiya-dashboard" element={<KaiyaDashboardPage />} />
              <Route path="/kaiya-evals" element={<KaiyaEvalsPage />} />
              <Route path="/kaiya-golden-docs" element={<KaiyaGoldenDocsPage />} />
              <Route path="/kaiya-guided-use-cases" element={<KaiyaGuidedUseCasesPage />} />
              <Route path="/kaiya-guided-use-cases/:canvasKey" element={<KaiyaGuidedCanvasPage />} />
              <Route path="/kaiya-guided-use-cases/:canvasKey/:slot" element={<KaiyaElementStandardEditorPage />} />
              <Route path="/kaiya-review-queue" element={<KaiyaReviewQueuePage />} />
              <Route path="/kaiya-playground" element={<KaiyaPlaygroundPage />} />

              {/* Agent Settings + Prompts — super only */}
              <Route element={<SuperAdminRoute />}>
                <Route path="/kaiya-settings" element={<KaiyaAgentSettingsPage />} />
                <Route path="/kaiya-prompts" element={<KaiyaPromptsPage />} />
              </Route>
            </Route>

            {/* Super Admin only routes */}
            <Route element={<SuperAdminRoute />}>
              <Route path="/admin-users" element={<AdminUsersPage />} />
            </Route>
          </Route>
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/user-list-by-tool" element={<UserListByToolReport />} />
          <Route path="/reports/portal-activity" element={<PortalActivityReport />} />
          <Route path="/reports/login-count" element={<LoginCountReport />} />
          <Route path="/reports/elearning-tracking" element={<ElearningTrackingReport />} />
          <Route path="/reports/user-list" element={<UserListReport />} />
          <Route path="/reports/workspace-entitlements" element={<WorkspaceEntitlementsReport />} />
          <Route path="/" element={<Navigate to="/users" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  // Initialize session auth service immediately
  useEffect(() => {
    sessionAuthService.initialize();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <TokenAuthHandler>
            <IntercomProvider>
              <ChameleonProvider>
                <AppRoutes />
                <Toaster />
              </ChameleonProvider>
            </IntercomProvider>
          </TokenAuthHandler>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;